import { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { supabase } from "../../integrations/supabase/client";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import {
  User,
  Phone,
  Mail,
  Save,
  Loader2,
  Shield,
  CheckCircle2,
} from "lucide-react";

interface ProfileData {
  full_name: string | null;
  phone: string | null;
}

export default function Profile() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState<ProfileData>({
    full_name: "",
    phone: "",
  });
  const [hasChanges, setHasChanges] = useState(false);
  const [originalProfile, setOriginalProfile] = useState<ProfileData>({
    full_name: "",
    phone: "",
  });

  useEffect(() => {
    const fetchProfile = async () => {
      if (!user) {
        return;
      }

      try {
        const { data, error } = await supabase
          .from("profiles")
          .select("full_name, phone")
          .eq("id", user.id)
          .maybeSingle();

        if (error) {
          throw error;
        }

        const profileData = {
          full_name: data?.full_name ?? "",
          phone: data?.phone ?? "",
        };
        setProfile(profileData);
        setOriginalProfile(profileData);
      } catch (error) {
        console.error("Error fetching profile:", error);
        toast.error("Failed to load profile");
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [user]);

  useEffect(() => {
    const changed =
      profile.full_name !== originalProfile.full_name ||
      profile.phone !== originalProfile.phone;
    setHasChanges(changed);
  }, [profile, originalProfile]);

  const handleSave = async () => {
    if (!user) {
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase
        .from("profiles")
        .update({
          full_name: profile.full_name?.trim() ?? null,
          phone: profile.phone?.trim() ?? null,
        })
        .eq("id", user.id);

      if (error) {
        throw error;
      }

      setOriginalProfile(profile);
      toast.success("Profile updated successfully");
    } catch (error) {
      console.error("Error updating profile:", error);
      toast.error("Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="space-y-4 text-center">
          <Loader2 className="mx-auto h-10 w-10 animate-spin text-primary" />
          <p className="text-muted-foreground">Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-foreground">My Profile</h1>
        <p className="text-muted-foreground">
          Manage your personal contact information
        </p>
      </div>

      {/* Account Info Card */}
      <Card className="overflow-hidden">
        <div className="bg-gradient-to-r from-primary/5 to-primary/10 px-6 py-6">
          <div className="flex items-center gap-4">
            <div className="flex h-16 w-16 items-center justify-center rounded-full border-2 border-primary/20 bg-primary/10">
              <User className="h-8 w-8 text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-semibold">
                {profile.full_name ?? "Institution Admin"}
              </h3>
              <div className="mt-1 flex items-center gap-2">
                <Badge
                  variant="secondary"
                  className="gap-1 border-0 bg-primary/10 text-primary"
                >
                  <Shield className="h-3 w-3" />
                  Institution Admin
                </Badge>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Contact Form */}
      <Card>
        <CardHeader className="border-b bg-muted/30">
          <CardTitle className="flex items-center gap-2 text-lg">
            <User className="h-5 w-5" />
            Contact Information
          </CardTitle>
          <CardDescription>
            This information will be used for communication purposes
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6 pt-6">
          <div className="space-y-2">
            <Label htmlFor="full_name" className="text-sm font-medium">
              Full Name
            </Label>
            <Input
              id="full_name"
              placeholder="Enter your full name"
              value={profile.full_name ?? ""}
              onChange={e =>
                setProfile({ ...profile, full_name: e.target.value })
              }
              maxLength={100}
              className="h-11"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone" className="text-sm font-medium">
              Phone Number
            </Label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="phone"
                type="tel"
                placeholder="Enter your phone number"
                value={profile.phone ?? ""}
                onChange={e =>
                  setProfile({
                    ...profile,
                    phone: e.target.value.replace(/[^\d+]/g, "").slice(0, 15),
                  })
                }
                className="h-11 pl-10"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className="text-sm font-medium">
              Email Address
            </Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="email"
                type="email"
                value={user?.email ?? ""}
                disabled
                className="h-11 bg-muted/50 pl-10"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Email address is linked to your account and cannot be changed
            </p>
          </div>

          <div className="border-t pt-4">
            <Button
              onClick={handleSave}
              disabled={saving || !hasChanges}
              className="h-11 w-full"
            >
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving Changes...
                </>
              ) : hasChanges ? (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Save Changes
                </>
              ) : (
                <>
                  <CheckCircle2 className="mr-2 h-4 w-4" />
                  All Changes Saved
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
